import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { UPDATE_ACCOUNT } from '../../graphql';
import { UpdateAccountInput } from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';
import { TIMEZONE_OPTIONS } from './Defaults';

function zoneLabel(label: string) {
  const [name, zone] = label.split(' - ');
  const short = name.replace(/\s*\([^)]*\)\s*/g, '').trim();
  return zone ? `${short} · ${zone}` : short;
}

export function OrganizationSettings() {
  const [organizationName, setOrganizationName] = useState<string>('');
  const [contactEmail, setContactEmail] = useState<string>('');
  const [timezone, setTimezone] = useState<string>('');

  const {
    selectedAccount: organization,
    isLoading: loading,
    error,
    refetchAccounts,
  } = useAccount();

  const [updateAccount, { loading: updating }] = useMutation(UPDATE_ACCOUNT, {
    onCompleted: () => {
      refetchAccounts();
      // TODO: Show success message
    },
    onError: (error) => {
      console.error('Error updating organization:', error);
      // TODO: Show error message
    },
  });

  // Initialize form with the selected account's organization data
  useEffect(() => {
    if (organization) {
      setOrganizationName(organization.name || '');
      setContactEmail(organization.techContactEmail || '');
      setTimezone(organization.timezone || '');
    } else {
      setOrganizationName('');
      setContactEmail('');
      setTimezone('');
    }
  }, [organization]);

  const dirty =
    organizationName !== (organization?.name || '') ||
    contactEmail !== (organization?.techContactEmail || '') ||
    timezone !== (organization?.timezone || '');

  const handleSave = async () => {
    if (!organization || !dirty) return;

    try {
      const updateInput: UpdateAccountInput = {
        name: organizationName.trim() || undefined,
        id: organization.id,
        isActive: organization.isActive || true,
        techContactEmail: contactEmail.trim() || undefined,
        timezone: timezone || '',
      };

      // Note: Additional fields (email, phone, website, address, etc.) are not yet
      // supported by the UpdateAccountInput schema. Once the backend schema is extended,
      // these fields can be added to the updateInput object.

      await updateAccount({
        variables: {
          id: organization.id,
          input: updateInput,
        },
      });
    } catch (error) {
      console.error('Error saving organization:', error);
    }
  };

  if (loading) return <p className="ink-lede">Loading…</p>;
  if (error) return <p className="ink-lede">Could not load this account.</p>;
  if (!organization) return <p className="ink-lede">No account found.</p>;

  return (
    <div>
      <h1 className="ink-h1">General</h1>
      <form
        className="ink-acct-form"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
      >
        <label>
          Organization name
          <input
            className="ink-acct-input"
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
          />
        </label>
        <label>
          Technical contact
          <input
            className="ink-acct-input"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
        </label>
        <label>
          Timezone
          <select
            className="ink-acct-input"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            <option value="">Select timezone</option>
            {TIMEZONE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {zoneLabel(option.label)}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          className="ink-btn ink-btn-primary"
          disabled={updating || !organizationName.trim()}
        >
          {updating ? 'Saving…' : 'Save'}
        </button>
      </form>
    </div>
  );
}
